import React, { useMemo, useState, useContext } from 'react';

import { Button, Col, Form, Row, Table } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import Select from 'react-select';
import { useHistory } from 'react-router-dom';

import AddProductURLsModal from 'components/product_enrichment_resources/AddProductURLsModal';
import { AuthContext } from 'components/providers/AuthContextProvider';
import { backgroundGrey } from 'lib/css/colors';
import Container from 'components/shared/Container';
import { DEFAULT_PAGE_SIZE } from 'lib/constants';
import EntityType, { ENTITY_TYPE_OPTIONS } from 'lib/enums/EntityType';
import {
  GLOBAL_MANUFACTURERS_FOR_DROPDOWN,
  GLOBAL_VENDORS_FOR_DROPDOWN,
  PRODUCT_ENRICHMENT_RESOURCES,
  PRODUCT_ENRICHMENT_RESOURCES_RETRY_PROCESSING,
  PRODUCT_ENRICHMENT_RESOURCES_UPDATE_QA,
} from 'lib/networking/endpoints';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import PaginationComponent from 'components/shared/PaginationComponent';
import pointerOnHover from 'lib/css/pointerOnHover';
import {
  ProductEnrichmentResourcesFileType,
  ProductEnrichmentResourcesFileTypeLabels,
} from 'lib/enums/ProductEnrichmentResourcesFileType';
import {
  ProductEnrichmentResourcesQAStatus,
  ProductEnrichmentResourcesQAStatusLabels,
} from 'lib/enums/ProductEnrichmentResourcesQAStatus';
import { ProductTypeDisplay } from 'lib/enums/ProductType';
import SearchableScrollableSelect from 'components/shared/SearchableScrollableSelect';
import { SuperchargedStatusLabels } from 'lib/enums/SuperchargedStatus';
import SwalDialog from 'lib/utils/toast';
import useGet from 'lib/hooks/useGet';
import usePost from 'lib/hooks/usePost';

function ProductEnrichmentResourcesTableView() {
  const history = useHistory();
  const { authUser } = useContext(AuthContext);
  const [entityType, setEntityType] = useState();
  const [entity, setEntity] = useState({});
  const [qaStatus, setQaStatus] = useState();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [showAddModal, setShowAddModal] = useState(false);

  const { data: { data: globalVendors } = {} } = useGet(GLOBAL_VENDORS_FOR_DROPDOWN);

  const entityNames = useMemo(() => {
    if (entityType?.value === EntityType.DISTRIBUTOR && globalVendors) {
      return globalVendors.map(vendor => ({
        value: vendor.id,
        label: vendor.name,
      }));
    }
    return [];
  }, [entityType, globalVendors]);

  const qaStatusOptions = useMemo(() => {
    return Object.entries(ProductEnrichmentResourcesQAStatusLabels).map(([value, label]) => ({
      value,
      label,
    }));
  }, []);

  const {
    data: { resource_records, count = 0 } = {},
    loading,
    refetch,
  } = useGet(PRODUCT_ENRICHMENT_RESOURCES, {
    file_type: ProductEnrichmentResourcesFileType.PRODUCT_URLS,
    ...(entityType?.value === EntityType.DISTRIBUTOR && { global_vendor_id: entity?.value }),
    ...(entityType?.value === EntityType.MANUFACTURER && { global_manufacturer_id: entity?.value }),
    ...(qaStatus && { qa_status: qaStatus.value }),
    limit: itemsPerPage,
    offset: itemsPerPage * (currentPage - 1),
  });

  const { postData: retryProcessing, loading: processingRetry } = usePost(
    PRODUCT_ENRICHMENT_RESOURCES_RETRY_PROCESSING,
    () => {
      SwalDialog('success', 'Successfully initiated resource processing', 'Success', 'center');
      refetch();
    },
    error =>
      SwalDialog(
        'error',
        error?.response?.data?.message || 'Failed to retry processing',
        'Error',
        'center',
      ),
  );

  const { postData: updateQAStatus, loading: updatingQA } = usePost(
    PRODUCT_ENRICHMENT_RESOURCES_UPDATE_QA,
    () => {
      SwalDialog('success', 'Successfully updated QA status', 'Success', 'center');
      refetch();
    },
    error =>
      SwalDialog.error(
        'error',
        error?.response?.data?.message || 'Failed to update QA status',
        'Error',
        'center',
      ),
  );

  const handleRetryProcessing = resourceId => {
    retryProcessing({
      resource_id: resourceId,
    });
  };

  const handleUpdateQAStatus = resourceId => {
    updateQAStatus({ resource_id: resourceId });
  };

  const handleAddProductURLsSuccess = () => {
    refetch();
    setShowAddModal(false);
    SwalDialog('success', 'Successfully added product URLs', 'Success', 'center');
  };

  const renderQAButton = resource_record => {
    if (resource_record.qa_status === ProductEnrichmentResourcesQAStatus.READY_FOR_QA) {
      return (
        <Button
          variant="link"
          className={pointerOnHover}
          disabled={updatingQA}
          onClick={() => handleUpdateQAStatus(resource_record.id)}
        >
          Start QA
        </Button>
      );
    }

    if (
      resource_record.qa_status === ProductEnrichmentResourcesQAStatus.IN_PROGRESS &&
      resource_record.qa_user.id === authUser.id
    ) {
      return (
        <Button
          variant="link"
          className={pointerOnHover}
          disabled={updatingQA}
          onClick={() => handleUpdateQAStatus(resource_record.id)}
        >
          Mark QA Completed
        </Button>
      );
    }

    return null;
  };

  if (loading) {
    return <LoadingSpinner short />;
  }

  return (
    <Container>
      <Row>
        <Col md={3}>
          <Form.Group className="mr-3">
            <Form.Label>Entity Type</Form.Label>
            <Select
              value={entityType}
              options={ENTITY_TYPE_OPTIONS}
              onChange={e => {
                setEntity({});
                setEntityType(e || {});
              }}
              isSearchable
              isClearable
              placeholder="Select Entity Type"
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Entity Name</Form.Label>
            {entityType?.value === EntityType.MANUFACTURER ? (
              <SearchableScrollableSelect
                initOption={entity}
                onChange={e => setEntity(e)}
                fetchUrl={GLOBAL_MANUFACTURERS_FOR_DROPDOWN}
                optionMapper={manufacturer => ({
                  value: manufacturer.id,
                  label: manufacturer.name,
                })}
                autoFocus={false}
                placeholder="Select Manufacturer"
              />
            ) : (
              <Select
                value={entity}
                options={entityNames}
                onChange={e => setEntity(e)}
                isSearchable
                isClearable
                placeholder="Select Distributor"
              />
            )}
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>QA Status</Form.Label>
            <Select
              value={qaStatus}
              options={qaStatusOptions}
              onChange={e => {
                setQaStatus(e);
                setCurrentPage(1);
              }}
              isSearchable
              isClearable
              placeholder="Select QA Status"
            />
          </Form.Group>
        </Col>
        <Col md={3} className="d-flex align-items-center justify-content-end">
          <Button variant="primary" onClick={() => setShowAddModal(true)} className="mb-3">
            Add Product URLs
          </Button>
        </Col>
      </Row>
      <Row className="mt-3 mb-2">
        <Col className="d-flex justify-content-between align-items-center">
          <span>{count} Resources found</span>
          <div className="d-flex justify-content-end px-3 py-3">
            <PaginationComponent
              totalItems={count}
              itemsPerPage={itemsPerPage}
              setItemsPerPage={setItemsPerPage}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              disabled={loading}
            />
          </div>
        </Col>
      </Row>
      <Table className={css(styles.tableText)}>
        <thead className={css(styles.thead)}>
          <tr>
            <th>Resource ID</th>
            <th>Created Date</th>
            <th>Entity Type</th>
            <th>Entity Name</th>
            <th>File Type</th>
            <th>Product Count</th>
            <th>Supercharged Status</th>
            <th>QA Status</th>
            <th>QA User</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody className={css(styles.tbody)}>
          {resource_records?.length ? (
            resource_records.map((resource_record, index) => (
              <tr key={index}>
                <td>{resource_record.id}</td>
                <td>{resource_record.date_created}</td>
                <td>
                  {resource_record.global_manufacturer
                    ? ProductTypeDisplay.MANUFACTURER
                    : ProductTypeDisplay.DISTRIBUTOR}
                </td>
                <td>
                  {resource_record.global_manufacturer?.name || resource_record.global_vendor?.name}
                </td>
                <td>{ProductEnrichmentResourcesFileTypeLabels[resource_record.file_type]}</td>
                <td>{resource_record.product_count || 0}</td>
                <td>{SuperchargedStatusLabels[resource_record.supercharged_status]}</td>
                <td>{ProductEnrichmentResourcesQAStatusLabels[resource_record.qa_status]}</td>
                <td>{resource_record.qa_user?.name || '-'}</td>
                <td>
                  <div className="d-flex">
                    {resource_record.data_source && (
                      <Button
                        variant="link"
                        className={pointerOnHover}
                        onClick={() =>
                          history.push(`/data-sources/${resource_record.data_source?.id}`)
                        }
                      >
                        QA
                      </Button>
                    )}
                    {resource_record.qa_status !== ProductEnrichmentResourcesQAStatus.COMPLETED && (
                      <Button
                        variant="link"
                        className={pointerOnHover}
                        disabled={processingRetry}
                        onClick={() => handleRetryProcessing(resource_record.id)}
                      >
                        {!resource_record.supercharged_key ? 'Process Now' : 'Retry Processing'}
                      </Button>
                    )}
                    {renderQAButton(resource_record)}
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="100%" className="text-center">
                No product enrichment resources found.
              </td>
            </tr>
          )}
        </tbody>
      </Table>
      <AddProductURLsModal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        onSuccess={handleAddProductURLsSuccess}
        refetch={refetch}
      />
    </Container>
  );
}

const styles = StyleSheet.create({
  tableText: {
    fontSize: '0.95rem',
  },
  thead: {
    position: 'sticky-top',
    zIndex: 1,
    backgroundColor: backgroundGrey,
  },
  tbody: {
    zIndex: 0,
    position: 'relative',
  },
});

export default ProductEnrichmentResourcesTableView;
